import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { formatPhoneNumber } from '../ops/components/PhoneInput';

const Contact = () => {
  const { settings, loading } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!settings) return;

    // Create email body
    const emailBody = `
CONTACT FORM SUBMISSION FROM BUILDBROZ WEBSITE
----------------------------------------------

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Subject: ${formData.subject}

Message:
${formData.message}

----------------------------------------------
Please respond to this inquiry as soon as possible.
    `.trim();

    // Create mailto link
    const subject = encodeURIComponent(`Contact: ${formData.subject}`);
    const body = encodeURIComponent(emailBody);
    const mailtoLink = `mailto:${settings.email}?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Reset form
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get In <span className="text-[#D4AF37]">Touch</span>
          </h1>
          <div className="w-20 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Have a question or ready to start your project? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information Cards */}
            <div className="space-y-6">
              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <Phone className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Phone</h3>
                    <a href={`tel:${settings.phone1.replace(/[^0-9]/g, '')}`} className="text-gray-400 hover:text-[#D4AF37] transition-colors block">
                      {formatPhoneNumber(settings.phone1)}
                    </a>
                    <a href={`tel:${settings.phone2.replace(/[^0-9]/g, '')}`} className="text-gray-400 hover:text-[#D4AF37] transition-colors block">
                      {formatPhoneNumber(settings.phone2)}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <Mail className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Email</h3>
                    <a href={`mailto:${settings.email}`} className="text-gray-400 hover:text-[#D4AF37] transition-colors">
                      {settings.email}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <MapPin className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Service Area</h3>
                    <p className="text-gray-400">
                      Serving {settings.service_area}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <Clock className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Business Hours</h3>
                    <p className="text-gray-400">Monday - Friday: 8am - 6pm</p>
                    <p className="text-gray-400">Saturday: 9am - 4pm</p>
                    <p className="text-gray-400">Sunday: Closed</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-[#1A1A1A] border-gray-800 p-8">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Send Us a <span className="text-[#D4AF37]">Message</span>
                </h2>
                <div className="w-20 h-1 bg-[#D4AF37] mb-8"></div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-white mb-2 block font-medium">Your Name</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="text-white mb-2 block font-medium">Email</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-white mb-2 block font-medium">Phone</label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange({ target: { name: 'phone', value: formatPhoneNumber(e.target.value) } })}
                        placeholder="330-316-7136"
                        required
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="text-white mb-2 block font-medium">Subject</label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Project Inquiry"
                        required
                        className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white mb-2 block font-medium">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      rows={6}
                      required
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold text-lg py-6"
                  >
                    Send Message
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="bg-[#2A2A2A] rounded-lg overflow-hidden h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d190255.26307132546!2d-81.87869584462384!3d41.47838033427131!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8830ef2ee3686b2d%3A0xed04cb55f7621842!2sCleveland%2C%20OH!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="BuildBroz Location - Cleveland, OH"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;